import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { ProfileInfoStep } from '@/components/signup/ProfileInfoStep';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackFunnelStep } from '@/utils/analytics';

const ProfileInfoPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  // Redirect if not authenticated or conditions not met
  React.useEffect(() => {
    if (!loading) {
      if (!user || !user.email_confirmed_at) {
        toast({
          title: "Authentication required",
          description: "Please sign in to complete your profile.",
          variant: "destructive",
        });
        navigate('/');
      }
    }
  }, [user, loading, navigate, toast]);

  const handleProfileComplete = async (profileData: any) => {
    if (!user) return;

    setIsLoading(true);
    
    try {
      // Save profile data and mark onboarding complete
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          dob: profileData.dob,
          marketing_opt_in: profileData.marketingOptIn,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;

      trackFunnelStep('account_created');
      
      toast({
        title: "Profile completed!",
        description: "Welcome to Safurra. Let's find you the perfect life insurance policy.",
      });
      
      // Redirect to dashboard
      navigate('/dashboard');
      
    } catch (error: any) {
      console.error('Profile save error:', error);
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !user.email_confirmed_at) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 bg-primary/10 p-3 rounded-full w-fit">
            <User className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            About you
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Complete your Safurra profile
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ProfileInfoStep
            onProfileComplete={handleProfileComplete}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileInfoPage;